/**
 * Minimal GraphQL-over-HTTP client built on the global `fetch`. Injects locale/site
 * headers plus an optional bearer token, and normalizes `errors[]` into a typed
 * {@link GraphQLDomainError}.
 */
import type { Locale } from "@mb-group/shared-types";
import { GraphQLDomainError, isDomainErrorCode, type FieldError } from "./errors.js";

/** Per-request context injected as headers (`x-mb-locale`, `x-mb-site`, bearer token). */
export interface RequestContext {
	locale: Locale;
	site: string;
	/** Optional bearer-token provider, invoked per request. */
	getToken?: () => string | undefined;
}

/** Options for {@link createClient}. */
export interface CreateClientOptions {
	/** Defaults to `import.meta.env.VITE_GRAPHQL_ENDPOINT` then `/graphql`. */
	endpoint?: string;
	ctx: RequestContext;
}

/** A thin GraphQL client. `query` and `mutate` share one transport. */
export interface GraphQLClient {
	readonly ctx: RequestContext;
	/** Execute a query document, resolving to its `data` payload. */
	query<T = unknown>(document: string, variables?: Record<string, unknown>): Promise<T>;
	/** Execute a mutation document, resolving to its `data` payload. */
	mutate<T = unknown>(document: string, variables?: Record<string, unknown>): Promise<T>;
}

interface GraphQLResponseError {
	message: string;
	path?: (string | number)[];
	extensions?: { code?: unknown; correlationId?: unknown; fields?: unknown };
}

interface GraphQLResponse<T> {
	data?: T;
	errors?: GraphQLResponseError[];
}

/** `import.meta.env` is injected by the bundler and not present in the TS lib. */
function envEndpoint(): string | undefined {
	const meta = import.meta as unknown as { env?: Record<string, string | undefined> };
	return meta.env?.VITE_GRAPHQL_ENDPOINT;
}

function toFieldErrors(value: unknown): FieldError[] | undefined {
	if (!Array.isArray(value)) return undefined;
	const fields: FieldError[] = [];
	for (const entry of value as unknown[]) {
		if (entry && typeof entry === "object" && "path" in entry && "message" in entry) {
			fields.push({
				path: String((entry as { path: unknown }).path),
				message: String((entry as { message: unknown }).message),
			});
		}
	}
	return fields.length > 0 ? fields : undefined;
}

function raise(errors: GraphQLResponseError[]): never {
	const first = errors[0];
	const message = first?.message ?? "GraphQL request failed";
	const ext = first?.extensions ?? {};
	const code = isDomainErrorCode(ext.code) ? ext.code : "INTERNAL";
	const correlationId = typeof ext.correlationId === "string" ? ext.correlationId : undefined;
	const fields = toFieldErrors(ext.fields);
	throw new GraphQLDomainError(code, message, {
		...(correlationId !== undefined ? { correlationId } : {}),
		...(fields !== undefined ? { fields } : {}),
	});
}

/**
 * Create a {@link GraphQLClient} bound to a request context and endpoint.
 *
 * @param options Endpoint (optional) and the {@link RequestContext} sent with each call.
 * @returns A client whose `query`/`mutate` throw {@link GraphQLDomainError} on failure.
 */
export function createClient(options: CreateClientOptions): GraphQLClient {
	const { ctx } = options;
	const endpoint = options.endpoint ?? envEndpoint() ?? "/graphql";

	async function execute<T>(document: string, variables?: Record<string, unknown>): Promise<T> {
		const headers: Record<string, string> = {
			"content-type": "application/json",
			accept: "application/json",
			"x-mb-locale": ctx.locale,
			"x-mb-site": ctx.site,
		};
		const token = ctx.getToken?.();
		if (token) headers.authorization = `Bearer ${token}`;

		let response: Response;
		try {
			response = await fetch(endpoint, {
				method: "POST",
				headers,
				body: JSON.stringify({ query: document, variables: variables ?? {} }),
			});
		} catch (cause) {
			throw new GraphQLDomainError(
				"UPSTREAM_UNAVAILABLE",
				cause instanceof Error ? cause.message : "Network request failed",
			);
		}

		if (!response.ok && response.status >= 500) {
			throw new GraphQLDomainError(
				"UPSTREAM_UNAVAILABLE",
				`Gateway responded ${String(response.status)}`,
			);
		}

		const payload = (await response.json()) as GraphQLResponse<T>;
		if (payload.errors && payload.errors.length > 0) raise(payload.errors);
		if (payload.data === undefined) {
			throw new GraphQLDomainError("INTERNAL", "GraphQL response contained no data");
		}
		return payload.data;
	}

	return {
		ctx,
		query<T = unknown>(document: string, variables?: Record<string, unknown>): Promise<T> {
			return execute<T>(document, variables);
		},
		mutate<T = unknown>(document: string, variables?: Record<string, unknown>): Promise<T> {
			return execute<T>(document, variables);
		},
	};
}
